"use client";
import { Anchor, List, Text, Title } from "@mantine/core";
import Link from "next/link";
import classes from "./legal.module.css";

export default function PrivacyPolicy() {
  return (
    <>
      <Title order={1} className={classes.legalTitle}>
        Privacy Policy
      </Title>
      <Text className={classes.legalText}>Last updated December 04, 2022</Text>

      <Text className={classes.legalText}>
        This website is operated by One Step Education Ltd CRN 13431410, who
        trade as Tiber Tutor (“Tiber Tutor”, “we”, “us”, “our”) under the domain
        name tibertutor.com (“the website”).
      </Text>
      <Text className={classes.legalText}>
        We are committed to protecting your online privacy and safety. This
        Privacy Policy explains what information we collect when you use Tiber
        Tutor, how the information will be used, and how you can control the
        collection, correction and/or deletion of this information. This Privacy
        Policy applies only to information you provide us through the Tiber
        Tutor website. Capitalized terms that are not defined in this Privacy
        Policy are defined in our{" "}
        <Anchor className={classes.legalLink} component={Link} href="/terms">
          Terms and Conditions
        </Anchor>
        .
        <Title order={2} className={classes.legalHeader}>
          What We Collect
        </Title>
        <Title order={3} className={classes.legalSubHeader}>
          Personal Information:
        </Title>
      </Text>
      <Text className={classes.legalText}>
        When you register to use the Tiber Tutor website, you provide us
        information about yourself, such as your name, username, e-mail address,
        geographical location, school, and subjects. You may also provide us
        with financial payment information, Unique Device ID (UDID), all subject
        to the applicable privacy settings, if a purchase is made. Tiber Tutor
        stores passwords in a secure database. This password information is
        encrypted and Tiber Tutor cannot access/ view these details.
        <Title order={3} className={classes.legalSubHeader}>
          Log File Information:
        </Title>
      </Text>
      <Text className={classes.legalText}>
        When you use the Service, our systems and servers automatically record
        certain log file information. These server logs may include information
        such as your web request, Internet Protocol (“IP”) address, browser
        type, referring / exit pages and URLs, number of clicks and how you
        interact with the Service, domain names, landing pages, pages viewed,
        and other such information.
        <Title order={3} className={classes.legalSubHeader}>
          Analytical Information:
        </Title>
      </Text>
      <Text className={classes.legalText}>
        When you use the Service, Tiber Tutor tracks the usage patterns of our
        users anonymously. The information is used to enable more accurate
        reporting, improve the effectiveness of our marketing, and make Tiber
        Tutor better for our users. Tiber Tutor uses third party analytics
        services like Google Analytics to help understand use of the Service.
        These third party analytics services collect the information sent by
        your usage of the Service.
        <Title order={2} className={classes.legalHeader}>
          Your Rights
        </Title>
      </Text>
      <Text className={classes.legalText}>
        As users of the Tiber Tutor website, you have the following rights about
        your data that you provide to Tiber Tutor during the user process:
      </Text>
      <List
        className={classes.legalList}
        styles={{ itemWrapper: { maxWidth: "calc(100% - 0.75em)" } }}
      >
        <List.Item>
          A right to erase: You have full authority for all your data to be
          completely erased from our servers. To take this action, either use
          the “Delete Account” button found at the bottom of the “Profile”
          section of the website, or contact our administrative team at{" "}
          <Anchor
            className={classes.legalLink}
            href="mailto:admin@tibertutor.com"
          >
            admin@tibertutor.com
          </Anchor>{" "}
          and the request will be processed within 48 hrs.
        </List.Item>
        <List.Item>
          A right to rectification: You have the right to rectify inaccurate
          personal data at any time, by using the “Profile” section of the
          website.
        </List.Item>
        <List.Item>
          A right to restrict processing: You have the right to restrict or
          suppress the personal data that Tiber Tutor holds. To do this, please
          email our administrative team at{" "}
          <Anchor
            className={classes.legalLink}
            href="mailto:admin@tibertutor.com"
          >
            admin@tibertutor.com
          </Anchor>{" "}
          with clear specifics of your request.
        </List.Item>
      </List>
      <Title order={2} className={classes.legalHeader}>
        How We Use Your Information
      </Title>
      <Text className={classes.legalText}>
        We use the personal information you submit to operate, maintain, and
        provide to you the features and functionality of the Service. By
        providing Tiber Tutor your email address and providing consent, you
        consent to our using the email address to send you Service-related
        notices and receipts, including any notices required by law, in lieu of
        communication by postal mail.
      </Text>
      <Text className={classes.legalText}>
        In addition, you also agree that we may send you notifications of
        activity on the Service, newsletters, changes to features of the
        Service, or special offers in accordance with any applicable privacy
        settings. If you do not want to receive such email messages, you may opt
        out by selecting “unsubscribe” at the bottom of our email
        correspondences. Opting out may prevent you from receiving certain
        messages, updates, improvements, or offers.
        <Title order={2} className={classes.legalHeader}>
          What We Do With Your Information
        </Title>
      </Text>
      <Text className={classes.legalText}>
        Tiber Tutor will not rent or sell your personally identifiable
        information to others, however may share your personally identifiable
        information with third parties for the purpose of providing the Service
        to you. If we do this, such third parties’ use of your information will
        be bound by this Privacy Policy.
      </Text>
      <Text className={classes.legalText}>
        We may store, transfer or assign your personal information in locations
        outside the direct control of Tiber Tutor (servers or databases of
        hosting providers) at any point in time or during the course of
        corporate divestitures, mergers, or dissolution. Tiber Tutor may
        disclose your personal information if required to do so by law or
        subpoena or if we believe that such action is necessary to:
      </Text>
      <List
        className={classes.legalList}
        styles={{ itemWrapper: { maxWidth: "calc(100% - 0.75em)" } }}
        type="ordered"
        listStyleType="lower-alpha"
      >
        <List.Item>
          conform to the law, comply with legal process served on us or our
          affiliates, or investigate, prevent, or take action regarding
          suspected or actual illegal activities;
        </List.Item>
        <List.Item>
          to enforce our Terms & Conditions, take precautions against liability,
          to investigate and defend ourselves against any third-party claims or
          allegations, to assist government enforcement agencies, or to protect
          the security or integrity of our site;
        </List.Item>
        <List.Item>
          and to exercise or protect the rights, property, or personal safety of
          Tiber Tutor, our Users or others.
        </List.Item>
      </List>
      <Text className={classes.legalText}>
        We may share anonymous usage data, referring/exit pages and URLs,
        platform types, number of clicks, etc. with interested third parties to
        help them understand the usage patterns of Tiber Tutor.
        <Title order={2} className={classes.legalHeader}>
          How We Protect Your Information
        </Title>
      </Text>
      <Text className={classes.legalText}>
        Tiber Tutor considers the integrity and security of your personal
        information of paramount importance. All your personally identifiable
        information is kept in encrypted form on a secured server behind a
        firewall. Additional automated and social measures are used to enhance
        security, such as analyzing account behavior for fraudulent or otherwise
        anomalous behavior. Tiber Tutor may limit use of site features in
        response to possible signs of abuse, remove inappropriate content or
        links to illegal content, and suspend or disable accounts for violations
        of our Terms of Use or Privacy Policy.
      </Text>
      <Text className={classes.legalText}>
        Although you may set privacy options that limit access to your
        information, no security measures are impenetrable and Tiber Tutor
        cannot control the actions of other users with whom you share your
        information. We cannot guarantee that only authorized persons will view
        your information and that information you share with Tiber Tutor will
        not become publicly available. We are not responsible for third party
        circumvention of any privacy settings or security measures on our
        website or associated systems or servers. Use of a strong password or
        different passwords for different services can reduce this risk. Your
        privacy settings may also be affected by changes to the functionality of
        Tiber Tutor’s distributors, such as social networks. Tiber Tutor is not
        responsible for the functionality or security measures of any third
        party.
        <Title order={2} className={classes.legalHeader}>
          Compromise of Personal Information
        </Title>
      </Text>
      <Text className={classes.legalText}>
        In the event that personal information is compromised, Tiber Tutor will
        promptly notify those persons whose personal information has been
        compromised, in accordance with the notification procedures set forth in
        this Privacy Policy, or as otherwise required by applicable law. Tiber
        Tutor does not knowingly collect or solicit personal information from
        anyone under the age of 15 or knowingly allow such persons to register
        as Members. If your age is below 15, please provide no information to
        us, including your name, email address, school, subjects, or address. No
        one under age 15 is allowed to use the Tiber Tutor website or provide
        any personal information to Tiber Tutor, and if you are identified to be
        in breach of this policy, without verification of parental consent, we
        will delete your information as quickly as possible. If you believe that
        we might have any information from or about a child under 15, please
        contact our administrative team at{" "}
        <Anchor
          className={classes.legalLink}
          href="mailto:admin@tibertutor.com"
        >
          admin@tibertutor.com
        </Anchor>{" "}
        .
      </Text>
      <Text className={classes.legalText}>
        We are not responsible for the practices employed by external websites
        or applications linked to from our website, nor the information or
        content contained therein. When using a link to visit such external
        websites or applications our Privacy Policy is no longer in effect and
        your browsing and interaction is subject to that website’s own rules and
        policies. Please read over those rules and policies before proceeding.
      </Text>
      <Text className={classes.legalText}>
        Tiber Tutor in its sole discretion reserves the right to determine the
        form and means of providing notifications to you, recognizing that you
        may opt out of certain means and types of notification as described in
        this Privacy Policy. Tiber Tutor may update its Privacy Policy from time
        to time and changes are effective when posted on this page. If you have
        any questions about this Privacy Policy, the practices of this site, or
        your dealings with this website, please contact us at{" "}
        <Anchor className={classes.legalLink} href="mailto:info@tibertutor.com">
          info@tibertutor.com
        </Anchor>{" "}
        , or send mail to: Tiber Tutor (One Step Education Ltd) Wedds Farmhouse,
        Wardsbrook Road, Ticehurst, TN5 7DP.
        <Title order={2} className={classes.legalHeader}>
          Google Adwords and Remarketing Policy
        </Title>
      </Text>
      <Text className={classes.legalText}>
        Tiber Tutor uses all the features of Google Analytics, including those
        for display Advertisers. This includes obtaining specific visitor cookie
        data, such as the source, medium and keyword used to visit our website.
        Google analytics does not store any visitor specific data and we will
        not use visitor specific data in any way related to Google Analytics,
        Google Adwords, and Remarketing. Whilst Tiber Tutor does not display
        advertisements in any way, other third-party vendors such, including
        Google, use first-party cookies (such as the Google Analytics Cookie)
        together to inform, optimize and serve ads based on visitor’s past
        visits to our website. Tiber Tutor does collect data on users Age,
        Gender and Interest to better identify our target demographics. None of
        the data collected can be used to identify individual users in any
        capacity.
      </Text>
    </>
  );
}

